import env from '@/config/env'
import { decodeJwt } from 'jose'
import { createTRPCClient, httpLink } from '@trpc/client'
import type { TrpcRouter } from '@backend/routes/trpc'

export const SESSION_TOKEN_KEY = 'ses'
export const REFRESH_TOKEN_KEY = 'ref'

const REFRESH_BUFFER_SECONDS = 60

export const getSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY)
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

export const setTokens = (sessionToken: string, refreshToken: string) => {
  localStorage.setItem(SESSION_TOKEN_KEY, sessionToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export const clearTokens = () => {
  localStorage.removeItem(SESSION_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const sessionTokenIsExpiringSoon = (sessionToken: string): boolean => {
  try {
    const { exp } = decodeJwt(sessionToken)
    if (typeof exp !== 'number') return true
    return exp - Math.floor(Date.now() / 1000) < REFRESH_BUFFER_SECONDS
  } catch {
    return true
  }
}

// Plain (non-batched, no interceptor) client used only for auth.refresh,
// to avoid recursive 401-handling when refreshing.
const refreshClient = createTRPCClient<TrpcRouter>({
  links: [httpLink({ url: env.VITE_BACKEND_URL })],
})

let inflightRefresh: Promise<{ sessionToken: string; refreshToken: string }> | null = null

const refreshOnce = async () => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new Error('No refresh token')

  const result = await refreshClient.auth.refresh.mutate({ refreshToken })
  setTokens(result.sessionToken, result.refreshToken)
  return result
}

export const refreshTokens = async () => {
  if (!inflightRefresh) {
    inflightRefresh = refreshOnce().finally(() => {
      inflightRefresh = null
    })
  }
  return await inflightRefresh
}

export const AUTH_LOGOUT_EVENT = 'auth:logout'
export const dispatchLogout = () => window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT))
